import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { useInView } from "../hooks/useInView";

const faqs = [
  {
    question: "How does Relio analyze my emails?",
    answer: "Relio connects to your Gmail via OAuth and reads email metadata and content to build relationship maps. All data is encrypted at rest and in transit. We never share your email content with third parties. Our AI processes emails to extract relationship signals like warmth scores, commitment tracking, and intro paths.",
  },
  {
    question: "What AI model powers the email drafting?",
    answer: "We use Anthropic's Claude Sonnet 4.5 for all AI-powered email drafting. Claude is trained to understand professional context, relationship nuance, and founder-specific communication patterns. Every draft is generated with full context of your past conversations with that contact.",
  },
  {
    question: "Can I use Relio with my team?",
    answer: "Yes! The Scale plan ($79/mo per user) includes multi-user collaboration features. Share notes, pipelines, and contacts across your founding team. Each team member gets their own AI drafting quota and can contribute to shared relationship graphs.",
  },
  {
    question: "How is Relio different from HubSpot or Salesforce?",
    answer: "Traditional CRMs are built for sales teams running playbooks. Relio is built for founders managing complex, high-stakes relationships. We don't ask you to manually log interactions. Instead, we passively analyze your email to surface relationship intelligence — warmth decay, intro paths, commitment tracking — that a standard CRM can't provide.",
  },
  {
    question: "Is my data secure?",
    answer: "Absolutely. We use SOC 2 Type II compliant infrastructure. All data is encrypted with AES-256 at rest and TLS 1.3 in transit. We never sell or share your data. You can export or delete all your data at any time. Our OAuth integration follows Google's strictest security guidelines.",
  },
  {
    question: "What happens when I hit my AI draft limit on Pro?",
    answer: "On the Pro plan, you get 100 AI email drafts per month. If you hit the limit, you can still use all other features. Drafts reset on the 1st of each month. If you need unlimited drafts, the Scale plan removes all limits for $79/mo per user.",
  },
  {
    question: "Can I import contacts from LinkedIn?",
    answer: "LinkedIn Data Ingestion is available on the Scale plan. You can sync contact data from LinkedIn profiles directly into Relio, enriching your relationship graph with professional context. This feature works alongside our Gmail integration to give you the most complete picture of your network.",
  },
];

export default function FAQSection() {
  const [sectionRef, isInView] = useInView();

  return (
    <section
      id="faq"
      data-testid="faq-section"
      ref={sectionRef}
      className="pl-8 md:pl-16 pr-4 md:pr-8 py-16 md:py-24"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        {/* Left - Header */}
        <div
          className={`lg:col-span-4 transition-all duration-700 ${
            isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <span className="text-xs uppercase tracking-[0.15em] font-semibold text-[#a33900] block mb-4">
            Support
          </span>
          <h2 className="text-3xl md:text-4xl font-medium tracking-tight text-[#1c1b1d]">
            Frequently asked
            <br />
            questions.
          </h2>
          <p className="mt-4 text-base text-[#5a4138]">
            Can't find what you're looking for?{" "}
            <a
              href="mailto:support@relio.in"
              className="text-[#a33900] underline underline-offset-2"
            >
              Contact support
            </a>
          </p>
        </div>

        {/* Right - Accordion */}
        <div
          className={`lg:col-span-8 transition-all duration-700 delay-200 ${
            isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <Accordion type="single" collapsible className="space-y-3">
            {faqs.map((faq, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                data-testid={`faq-item-${i}`}
                className="bg-[#f6f2f5] rounded-2xl px-6 border-none overflow-hidden hover:bg-[#f0edf0] transition-colors"
              >
                <AccordionTrigger
                  data-testid={`faq-trigger-${i}`}
                  className="text-sm font-medium text-[#1c1b1d] hover:no-underline py-5"
                >
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-sm text-[#5a4138] leading-relaxed pb-5">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}
